import { Fragment, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { getCapitalInjections, initiateCapitalInjection } from '../../services/CapitalInjectionService'
import { ROLE_SLUGS } from '../../utils/auth/permissionConstants'
import { scrollToPageTop } from '../../utils/scrollToPageTop'
import CapitalInjectionReveal from './CapitalInjectionReveal'
import { clickableRowProps } from '../../utils/clickableRow'

const CapitalInjectionRevealRow = ({ children }) => {
    const [isRevealOpen, setIsRevealOpen] = useState(false)
    return children({ isRevealOpen, setIsRevealOpen })
}

const EMPTY_FORM = { amount: '', phoneNumber: '', description: '' }

const formatAmount = (amount) =>
    `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })

const InjectedByCell = ({ injectedBy }) => (
    <span style={{ overflowWrap: 'anywhere' }}>
        {injectedBy?.name ?? '-'}
        {injectedBy?.is_deleted && (
            <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>(deleted)</span>
        )}
    </span>
)

const AmountCell = ({ amount }) =>
    amount != null
        ? formatAmount(amount)
        : <span className="text-muted fst-italic">Pending</span>

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    )
}

const CapitalInjections = () => {
    const queryClient = useQueryClient()
    const userRole = useSelector((state) => state.auth.authUser?.role_slug)

    const [showInitiateModal, setShowInitiateModal] = useState(false)
    const [form, setForm]                           = useState(EMPTY_FORM)
    const [page, setPage]                           = useState(1)

    const { data, isPending, isError, isFetching } = useQuery({
        queryKey: ['capital-injections', page],
        queryFn: () => getCapitalInjections(page),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: userRole === ROLE_SLUGS.EXEC_ADMIN,
    })

    const injections = data?.data?.data ?? []
    const lastPage   = data?.data?.meta?.last_page ?? 1

    const initiateMutation = useMutation({
        mutationFn: (payload) => initiateCapitalInjection(payload),
        onSuccess: async () => {
            toast.success('STK push sent. Please complete the payment on your phone.')
            setPage(1)
            await queryClient.invalidateQueries(['capital-injections'])
            setShowInitiateModal(false)
            setForm(EMPTY_FORM)
        },
        onError: (err) => {
            const message = err?.response?.data?.message || 'Failed to initiate capital injection.'
            toast.error(message)
        },
    })

    const handleInitiate = (e) => {
        e.preventDefault()
        initiateMutation.mutate({
            amount: parseInt(form.amount, 10),
            phoneNumber: form.phoneNumber,
            description: form.description || undefined,
        })
    }

    const handleClose = () => {
        setShowInitiateModal(false)
        setForm(EMPTY_FORM)
    }

    if (userRole !== ROLE_SLUGS.EXEC_ADMIN) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-muted mb-0">You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex align-items-center gap-2">
                            <h4 className="card-title mb-0">Capital Injections</h4>
                            <button
                                type="button"
                                onClick={() => setShowInitiateModal(true)}
                                className="btn btn-sm btn-primary ms-auto d-none d-lg-inline-flex align-items-center"
                            >
                                <i className="fa fa-plus me-1" aria-hidden="true" />
                                Inject Capital
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowInitiateModal(true)}
                                className="btn btn-primary rounded-circle ms-auto d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm"
                                style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                                title="Inject capital"
                                aria-label="Inject capital"
                            >
                                <i className="fas fa-plus" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="card-body p-0">
                            {isPending ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : isError ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load capital injections. Please try again.
                                </p>
                            ) : injections.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    No capital injections recorded yet.
                                </p>
                            ) : (
                                <>
                                    <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Amount</th>
                                                    <th scope="col">Injected By</th>
                                                    <th scope="col">Description</th>
                                                    <th scope="col">Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {injections.map((injection) => (
                                                    <CapitalInjectionRevealRow key={injection.id}>
                                                        {({ isRevealOpen, setIsRevealOpen }) => (
                                                            <tr {...clickableRowProps(() => setIsRevealOpen(true))}>
                                                                <td className="text-nowrap fw-semibold">
                                                                    <span className="d-inline-flex align-items-center gap-2">
                                                                        <AmountCell amount={injection.amount} />
                                                                        <CapitalInjectionReveal injection={injection} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                                                    </span>
                                                                </td>
                                                                <td className="text-nowrap">
                                                                    <InjectedByCell injectedBy={injection.injected_by} />
                                                                </td>
                                                                <td style={{ maxWidth: '320px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                    {injection.description || '-'}
                                                                </td>
                                                                <td className="text-nowrap">{formatDate(injection.created_at)}</td>
                                                            </tr>
                                                        )}
                                                    </CapitalInjectionRevealRow>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Injection</th>
                                                    <th scope="col" className="text-end">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {injections.map((injection) => (
                                                    <CapitalInjectionRevealRow key={injection.id}>
                                                        {({ isRevealOpen, setIsRevealOpen }) => (
                                                            <tr {...clickableRowProps(() => setIsRevealOpen(true))}>
                                                                <td>
                                                                    <div className="small text-muted">
                                                                        <i className="bi bi-person me-1" aria-hidden="true" />
                                                                        <InjectedByCell injectedBy={injection.injected_by} />
                                                                    </div>
                                                                    {injection.description && (
                                                                        <div className="mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {injection.description}
                                                                        </div>
                                                                    )}
                                                                    <div className="small text-muted mt-1">
                                                                        <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                                        {formatDate(injection.created_at)}
                                                                    </div>
                                                                </td>
                                                                <td className="text-nowrap text-end fw-semibold">
                                                                    <span className="d-inline-flex align-items-center gap-2">
                                                                        <AmountCell amount={injection.amount} />
                                                                        <CapitalInjectionReveal injection={injection} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </CapitalInjectionRevealRow>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <ul className="list-group list-group-flush d-block d-md-none">
                                        {injections.map((injection) => (
                                            <CapitalInjectionRevealRow key={injection.id}>
                                                {({ isRevealOpen, setIsRevealOpen }) => (
                                                    <li
                                                        className="list-group-item py-3"
                                                        {...clickableRowProps(() => setIsRevealOpen(true))}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-start gap-2">
                                                            <div className="fw-semibold text-body"><AmountCell amount={injection.amount} /></div>
                                                            <CapitalInjectionReveal injection={injection} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                                        </div>
                                                        <div className="small text-muted mt-1">
                                                            <i className="bi bi-person me-1" aria-hidden="true" />
                                                            <InjectedByCell injectedBy={injection.injected_by} />
                                                        </div>
                                                        {injection.description && (
                                                            <div className="mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {injection.description}
                                                            </div>
                                                        )}
                                                        <div className="small text-muted mt-1">
                                                            <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                            {formatDate(injection.created_at)}
                                                        </div>
                                                    </li>
                                                )}
                                            </CapitalInjectionRevealRow>
                                        ))}
                                    </ul>
                                </>
                            )}
                            <PaginationBar
                                page={page}
                                lastPage={lastPage}
                                isLoading={isFetching}
                                onPrev={() => { setPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                                onNext={() => { setPage(p => Math.min(p + 1, lastPage)); scrollToPageTop(); }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showInitiateModal} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Inject Capital</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">
                        An STK push will be sent to the phone number below. Complete the M-PESA prompt to record a capital injection into the owner's capital account.
                    </p>
                    <form id="inject-capital-form" onSubmit={handleInitiate}>
                        <div className="mb-3">
                            <label className="form-label">Amount (KES) <span className="text-danger">*</span></label>
                            <input
                                type="number"
                                className="form-control"
                                placeholder="e.g. 100000"
                                min="5"
                                step="1"
                                value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                required
                            />
                            {form.amount !== '' && parseInt(form.amount, 10) < 5 && (
                                <small className="text-danger mt-1 d-block">Minimum amount is KES 5.</small>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                            <input
                                type="tel"
                                className="form-control"
                                placeholder="e.g. 0712345678"
                                pattern="^0\d{9}$"
                                value={form.phoneNumber}
                                onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                                required
                            />
                            <div className="form-text">Kenyan number starting with 0 (10 digits).</div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Description <span className="text-muted">(optional)</span></label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="e.g. Initial float top-up for Q2"
                                maxLength={500}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                        </div>
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={initiateMutation.isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="inject-capital-form"
                        variant="primary"
                        disabled={initiateMutation.isPending || !form.amount || parseInt(form.amount, 10) < 5 || !form.phoneNumber}
                    >
                        {initiateMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Sending...
                            </>
                        ) : (
                            'Send STK Push'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    )
}

export default CapitalInjections
